import React, { useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { ThemeProvider } from 'styled-components';
import { ConfigProvider } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { AppDispatch } from '../../../redux/store';
import {
  fetchStaffManagementReport,
  fetchDailyOperationsReport,
  fetchCustomerFeedbackReport,
} from '../../../redux/ManagerReports';
import { useTheme } from '../../../context/ThemeContext';
import { getAntdThemeConfig } from '../../../utils/antdTheme';
import {
  REPORT_SECTIONS,
  REPORT_SECTIONS_CONFIG,
  type ReportSectionType,
} from './constants';
import {
  Container,
  HeaderSection,
  MainTitle,
  ReportsGrid,
  ReportCard,
  CardHeader,
  IconWrapper,
  CardTitle,
  CardDescription,
  StyledCollapse,
} from './Styles';
import StaffManagementSection from './StaffManagementSection';
import DailyOperationsSection from './DailyOperationsSection';
import CustomerFeedbackSection from './CustomerFeedbackSection';

const ManagerReports: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { isDarkMode } = useTheme();
  const theme = React.useMemo(
    () => ({ mode: (isDarkMode ? 'dark' : 'light') as 'light' | 'dark' }),
    [isDarkMode]
  );

  const [selectedSection, setSelectedSection] = useState<ReportSectionType | null>(null);

  const handleSectionClick = useCallback(
    (sectionId: ReportSectionType) => {
      const newSelectedSection = selectedSection === sectionId ? null : sectionId;
      setSelectedSection(newSelectedSection);

      // Fetch data when section is opened
      if (newSelectedSection) {
        switch (newSelectedSection) {
          case REPORT_SECTIONS.STAFF_MANAGEMENT:
            dispatch(fetchStaffManagementReport());
            break;
          case REPORT_SECTIONS.DAILY_OPERATIONS:
            dispatch(fetchDailyOperationsReport());
            break;
          case REPORT_SECTIONS.CUSTOMER_FEEDBACK:
            dispatch(fetchCustomerFeedbackReport());
            break;
          default:
            break;
        }
      }
    },
    [dispatch, selectedSection]
  );

  return (
    <ThemeProvider theme={theme}>
      <ConfigProvider theme={getAntdThemeConfig(isDarkMode)}>
        <Container>
          <HeaderSection>
            <MainTitle level={1}>Reports</MainTitle>
          </HeaderSection>

          <ReportsGrid>
            {REPORT_SECTIONS_CONFIG.map((section) => (
              <ReportCard
                key={section.id}
                isSelected={selectedSection === section.id}
                cardColor={section.color}
                onClick={() => handleSectionClick(section.id)}
              >
                <CardHeader>
                  <IconWrapper color={section.color}>
                    <FontAwesomeIcon icon={section.icon} />
                  </IconWrapper>
                  <div>
                    <CardTitle level={4}>{section.title}</CardTitle>
                    <CardDescription>{section.description}</CardDescription>
                  </div>
                </CardHeader>
              </ReportCard>
            ))}
          </ReportsGrid>

          <StyledCollapse
            activeKey={selectedSection || []}
            onChange={(keys) => {
              const activeKey = Array.isArray(keys) ? keys[0] : keys;
              handleSectionClick(activeKey as ReportSectionType);
            }}
          >
            {REPORT_SECTIONS_CONFIG.map((section) => (
              <StyledCollapse.Panel
                key={section.id}
                header={section.title}
                showArrow={false}
              >
                {selectedSection === REPORT_SECTIONS.STAFF_MANAGEMENT &&
                  section.id === REPORT_SECTIONS.STAFF_MANAGEMENT && (
                    <StaffManagementSection />
                  )}
                {selectedSection === REPORT_SECTIONS.DAILY_OPERATIONS &&
                  section.id === REPORT_SECTIONS.DAILY_OPERATIONS && (
                    <DailyOperationsSection />
                  )}
                {selectedSection === REPORT_SECTIONS.CUSTOMER_FEEDBACK &&
                  section.id === REPORT_SECTIONS.CUSTOMER_FEEDBACK && (
                    <CustomerFeedbackSection />
                  )}
              </StyledCollapse.Panel>
            ))}
          </StyledCollapse>
        </Container>
      </ConfigProvider>
    </ThemeProvider>
  );
};

export default React.memo(ManagerReports);
